import Http from '@shared/http'

export const buildResourcefulRequests = (basePath) => {
  return {
    // React Query passes a context object to query functions. It is not a
    // query-string payload; serialising it produces `?[object Object]`.
    index: async (queryParams = '') => {
      const query = typeof queryParams === 'string' ? queryParams : ''
      return await executeRequest(query ? `${basePath}?${query}` : basePath)
    },
    show: async (id) => await executeRequest(`${basePath}/${id}`),
    update: async (resource = {}) => await executeRequest(
      `${basePath}/${resource.id || ''}`,
      'PUT',
      JSON.stringify(resource)
    ),
    updateWithId: async (payload) => {
      return await executeRequest(
        `${basePath}/${payload.id || ''}`,
        'PUT',
        JSON.stringify(payload.resource)
      )
    },
    create: async (resource = {}) => await executeRequest(
      basePath,
      'POST',
      JSON.stringify(resource)
    ),
    delete: async (id) => await executeRequest(`${basePath}/${id}`, 'DELETE')
  }
}

export const buildSingularResourcefulRequests = (basePath) => {
  return {
    show: async () => await executeRequest(basePath),
    update: async (resource = {}) => await executeRequest(basePath, 'PUT', JSON.stringify(resource)),
    create: async (resource = {}) => await executeRequest(basePath, 'POST', JSON.stringify(resource)),
    delete: async () => await executeRequest(basePath, 'DELETE')
  }
}

const STATUS_EXPLANATIONS = {
  401: 'Your session has expired. Sign in again and retry.',
  403: 'You do not have permission to do that.',
  404: 'The server could not find this record. It may have been changed or removed since this page was loaded — reload and try again.',
  409: 'This record was changed somewhere else. Reload and try again.',
  422: 'The server rejected this change.',
  500: 'Something went wrong on the server. If it keeps happening, please report it.'
}

// Controllers answer a failure with `{ error: "..." }`. Carrying that through
// means callers can say what actually went wrong instead of "please try again".
const errorMessage = async (response) => {
  // A manually-followed redirect can arrive as an opaque response with no
  // status or status text. Do not surface an empty error to the user.
  if (!response.status) return 'The request could not be completed. Reload the page and try again.'

  const fallback = STATUS_EXPLANATIONS[response.status] ||
    response.statusText ||
    `Request failed (${response.status})`

  try {
    const body = await response.json()
    return body?.error || body?.message || fallback
  } catch {
    return fallback
  }
}

// 204 No Content — and any other empty body — has nothing to parse. Returning
// null keeps DELETEs on the success path: controllers answer them with
// `head :no_content`, and parsing that as JSON used to throw, so a delete that
// had already succeeded surfaced to the UI as a failed request.
const parseBody = async (response) => {
  if (response.status === 204) return null

  const text = await response.text()
  return text ? JSON.parse(text) : null
}

export const executeRequest = async (endpoint, method = 'GET', body = null) => {
  const response = await Http.request(
    endpoint,
    method,
    body
  )

  if (!response.ok) throw new Error(await errorMessage(response))

  return await parseBody(response)
}
